/**
 * §P.2 · Los matriculados de un grupo.
 *
 * La regla que da forma a esta pantalla es el `409`:
 *
 * > **`409 group_full` trae el aforo en el `detail`.** No lo pintéis como error a
 * > secas: es el momento de ofrecer otro grupo con hueco, y tenéis
 * > `enrolled_count`/`capacity` de todos en la lista de grupos para hacerlo en la
 * > misma pantalla.
 *
 * Así que cuando el grupo está lleno, esto **no** enseña un error rojo: enseña los
 * grupos con hueco. Quien está al teléfono con una familia nueva puede resolverlo
 * sin colgar.
 *
 * Y la baja **pone fecha, no borra**: «el niño estuvo ahí y eso sigue siendo
 * verdad: su asistencia de octubre solo se explica si consta que en octubre era
 * del grupo». El botón dice «Dar de baja del grupo».
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { fecha } from '@/shared/ui/formato';
import { nombreCompleto, type PaginaDeAlumnos } from '@/club/alumnos/tipos';
import {
  estaCompleto,
  ocupacion,
  type Grupo,
  type Matricula,
} from '@/club/grupos/tipos';

export function Matriculados({
  club,
  grupo,
  puedeEditar,
  alAbrirAlumno,
}: {
  club: string;
  grupo: Grupo;
  puedeEditar: boolean;
  alAbrirAlumno: (alumnoId: string) => void;
}) {
  const cache = useQueryClient();
  const [matriculando, setMatriculando] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [problema, setProblema] = useState<Problema | null>(null);
  const [conBajas, setConBajas] = useState(false);

  const matriculas = useQuery({
    queryKey: ['matriculas', club, grupo.id, conBajas],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .groups({ groupId: grupo.id })
        .enrollments.get({ query: conBajas ? { include_left: true } : {} });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Matricula[];
    },
  });

  // Los candidatos: alumnos activos del club que buscar por nombre.
  const candidatos = useQuery({
    queryKey: ['alumnos', club, { q: busqueda, status: 'active' }, null],
    enabled: matriculando && busqueda.length >= 2,
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).students.get({
        query: { limit: 10, status: 'active', q: busqueda },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as PaginaDeAlumnos;
    },
  });

  // Para ofrecer alternativas cuando el grupo esté lleno.
  const grupos = useQuery({
    queryKey: ['grupos', club, false],
    enabled: problema?.code === 'group_full',
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).groups.get({
        query: {},
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Grupo[];
    },
  });

  const refrescar = () => {
    void cache.invalidateQueries({ queryKey: ['matriculas', club, grupo.id] });
    void cache.invalidateQueries({ queryKey: ['grupos', club] });
    void cache.invalidateQueries({ queryKey: ['grupo', club, grupo.id] });
  };

  const matricular = useMutation({
    mutationFn: async (studentId: string) => {
      const r = await api.v1
        .clubs({ club })
        .groups({ groupId: grupo.id })
        .enrollments.post({ student_id: studentId });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setProblema(null);
      setBusqueda('');
      setMatriculando(false);
      refrescar();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const darDeBaja = useMutation({
    mutationFn: async (enrollmentId: string) => {
      const r = await api.v1
        .clubs({ club })
        .enrollments({ enrollmentId })
        .delete();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: refrescar,
    onError: (e) => setProblema(aProblema(e)),
  });

  if (matriculas.isPending)
    return <Cargando filas={3} etiqueta="Cargando matriculados" />;

  if (matriculas.isError)
    return (
      <PanelError
        problema={aProblema(matriculas.error)}
        reintentar={() => void matriculas.refetch()}
      />
    );

  const lleno = problema?.code === 'group_full';
  const conHueco = (grupos.data ?? []).filter(
    (g) => g.id !== grupo.id && g.active && !estaCompleto(g)
  );

  return (
    <>
      <div className="titulo-fila">
        <p className="tenue" style={{ margin: 0 }}>
          {ocupacion(grupo)}{' '}
          {grupo.capacity === null ? 'matriculados' : 'plazas'}
          {estaCompleto(grupo) && ' · completo'}
        </p>
        {puedeEditar && !matriculando && (
          <button
            type="button"
            className="btn-plano"
            onClick={() => setMatriculando(true)}
          >
            Matricular a alguien
          </button>
        )}
      </div>

      {matriculando && (
        <div className="buscador">
          <label>
            <span className="sr">Buscar alumno</span>
            <input
              type="search"
              autoFocus
              placeholder="Nombre, apellido o número de socio"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </label>

          {/* El rango se enseña, no se impone: un aviso suave y nada más. */}
          {grupo.age_min !== null || grupo.age_max !== null ? (
            <p className="tenue">
              Este grupo es de {grupo.age_min ?? '?'} a {grupo.age_max ?? '?'}{' '}
              años, pero puedes matricular a quien quieras: es orientativo.
            </p>
          ) : null}

          {candidatos.data?.data.length === 0 && busqueda.length >= 2 && (
            <p className="tenue">Ningún alumno activo encaja con eso.</p>
          )}

          {(candidatos.data?.data ?? []).map((a) => (
            <button
              key={a.id}
              type="button"
              className="btn-plano candidato"
              disabled={matricular.isPending}
              onClick={() => matricular.mutate(a.id)}
            >
              {nombreCompleto(a)}
              {a.code ? ` · ${a.code}` : ''}
            </button>
          ))}

          <button
            type="button"
            className="btn-plano"
            onClick={() => {
              setMatriculando(false);
              setBusqueda('');
              setProblema(null);
            }}
          >
            Cancelar
          </button>
        </div>
      )}

      {/*
        El grupo lleno NO es un error rojo: es el momento de ofrecer otro grupo.
        El `detail` del servidor ya trae el número de plazas, y se enseña tal cual.
      */}
      {lleno ? (
        <div className="aviso" role="status">
          <strong>{problema.title}</strong> {problema.detail}
          {conHueco.length > 0 ? (
            <>
              <p style={{ margin: '0.6rem 0 0.3rem' }}>
                Estos tienen hueco ahora mismo:
              </p>
              <ul className="lista-clara">
                {conHueco.map((g) => (
                  <li key={g.id}>
                    <strong>{g.name}</strong> — {ocupacion(g)}
                    {g.venue_name ? ` · ${g.venue_name}` : ''}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p style={{ margin: '0.6rem 0 0' }}>
              Y ningún otro grupo abierto tiene hueco. Toca subir el aforo o
              abrir otro grupo.
            </p>
          )}
        </div>
      ) : (
        problema && <PanelError problema={problema} />
      )}

      {matriculas.data.length === 0 ? (
        <Vacio
          titulo="Nadie matriculado todavía"
          explicacion="Un grupo sin alumnos genera clases vacías. Matricula a quien vaya a esta clase."
        />
      ) : (
        <>
          <ul className="matriculados">
            {matriculas.data.map((m) => (
              <li key={m.id} className={m.left_on ? 'fila-fuera' : undefined}>
                <button
                  type="button"
                  className="enlace-fila"
                  onClick={() => alAbrirAlumno(m.student_id)}
                >
                  {nombreCompleto(m)}
                </button>

                <span className="tenue">desde {fecha(m.joined_on)}</span>

                {m.left_on ? (
                  <span className="pastilla pastilla-left">
                    De baja el {fecha(m.left_on)}
                  </span>
                ) : (
                  puedeEditar && (
                    <button
                      type="button"
                      className="btn-plano"
                      disabled={darDeBaja.isPending}
                      onClick={() => darDeBaja.mutate(m.id)}
                    >
                      Dar de baja del grupo
                    </button>
                  )
                )}
              </li>
            ))}
          </ul>

          <label className="interruptor">
            <input
              type="checkbox"
              checked={conBajas}
              onChange={(e) => setConBajas(e.target.checked)}
            />
            Enseñar también a quien se dio de baja
          </label>
        </>
      )}
    </>
  );
}
